import { Directive, HostListener, OnDestroy } from '@angular/core';

/**
 * iOS 软键盘修复（内联输入框版）：
 *
 * 内联输入框（非 fixed 弹层）聚焦时，键盘弹出会压缩 visualViewport（--app-height
 * 随之变小），同时 iOS 会自动滚动带 `-webkit-overflow-scrolling: touch` 的滚动
 * 容器去"露出"输入框。两者赛跑，滚动位置被顶到错误的地方——表现就是
 * "输入时输入框飘走/看不见，关掉键盘才恢复"（安卓无软键盘顶起问题，无需处理）。
 *
 * 做法：聚焦后等键盘动画结束（约 300ms），再把当前聚焦的输入框
 * `scrollIntoView({ block: 'nearest' })` 滚回可视区。nearest 只在输入框被键盘
 * 遮住时才滚动，已在可视区内则不动，避免打断用户正在进行的滚动。
 *
 * 挂在容器上，用 focusin（冒泡）捕获其内所有输入框的聚焦，避免逐个 input 加。
 *
 * 快速 focus in / focus out 的兜底：focusout 会立刻取消尚未触发的滚动，
 * 且定时器触发时会再确认目标仍是当前聚焦元素。否则 blur 后 ~300ms 遗留的
 * scrollIntoView 会在 iOS 收键盘 / 恢复滚动 / --app-height 变化期间把页面
 * 重新拉回刚失焦的输入框，与系统滚动互相打架，来回几次输入框就被顶出可视区
 * （"飘出去不见了"）。
 */
@Directive({ selector: '[appFocusKeepVisible]' })
export class FocusKeepVisibleDirective implements OnDestroy {
  private readonly isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent);
  private scrollTimer: ReturnType<typeof setTimeout> | null = null;

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent): void {
    if (!this.isIOS) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!this.isEditable(target)) return;

    // 同一容器内快速切换输入框时，前一个输入框的滚动任务已无意义，先取消，
    // 避免多个过期的 scrollIntoView 排队触发、反复争夺滚动位置。
    this.cancelPendingScroll();

    this.scrollTimer = setTimeout(() => {
      this.scrollTimer = null;
      // 只在目标仍然聚焦时才滚动：blur 后键盘正在收起、iOS 正在恢复滚动，
      // 此时再对已失焦的输入框 scrollIntoView 会把页面拉回错误位置。
      if (
        target.isConnected &&
        document.activeElement === target &&
        document.hasFocus()
      ) {
        target.scrollIntoView({ block: 'nearest' });
      }
    }, 300);
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.cancelPendingScroll();
  }

  private cancelPendingScroll(): void {
    if (this.scrollTimer !== null) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
  }

  ngOnDestroy(): void {
    // 容器被 @if 移除时（如切 Tab 销毁卡片），清除遗留定时器，避免对已脱离
    // DOM 的输入框做无谓的滚动。
    this.cancelPendingScroll();
  }

  private isEditable(el: HTMLElement): boolean {
    const tag = el.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || el.isContentEditable;
  }
}
